import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Profile, ProfileApiResponse, ProfileDto } from '../models/Profile';
import { Group, GroupApiResponse, GroupDto } from '../models/Group';
import { Permission, PermissionApiResponse, PermissionDto } from '../models/Permission';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

export interface ProfileWithGroupsCreate {
  name: string;
  description?: string;
  groupIds?: number[];
  createdBy: string;
}

export interface CustomGroup {
  name: string;
  description?: string;
  permissions: {
    name: string;
    description: string;
  }[];
}

export interface ProfileWithCustomGroupsCreate {
  name: string;
  description?: string;
  createdBy: string;
  existingGroupIds?: number[];
  customGroups?: CustomGroup[];
}

export interface SpecificPermission {
  groupId: number;
  basePermissionName: string;
  customName: string;
  customDescription: string;
}

export interface ProfileWithSpecificPermissionsCreate {
  name: string;
  description?: string;
  createdBy: string;
  groupIds?: number[];
  specificPermissions?: SpecificPermission[];
}

export interface AssociateSpecificPermissionsRequest {
  profileId: number;
  permissionIds: number[];
  updatedBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProfilesService {
  // Usa proxy em desenvolvimento e host do microserviço em produção
   private baseUrl = environment.microservices.profileGroup;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  /**
   * Busca todos os perfis
   */
  getProfiles(page: number = 1, pageSize: number = 10): Observable<ProfileApiResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfileApiResponse>(`${this.baseUrl}/GetProfiles`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar perfis');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os perfis e retorna apenas os dados mapeados
   */
  getAllProfiles(): Observable<Profile[]> {
    return this.getProfiles(1, 1000).pipe(
      map(response => {
        // Validação para evitar erro de undefined
        if (!response || !response.data) {
          return [];
        }
        return response.data.map(dto => this.mapFromProfileDTO(dto));
      }),
      catchError(err => {
        console.error('❌ [ProfilesService] Erro ao buscar todos os perfis:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca perfil por ID
   */
  getProfileById(id: number): Observable<Profile> {
    return this.http.get<ProfileDto>(`${this.baseUrl}/GetProfile/${id}`).pipe(
      map(dto => this.mapFromProfileDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os grupos (groups)
   */
  getGroups(page: number = 1, pageSize: number = 10): Observable<GroupApiResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<GroupApiResponse>(`${this.baseUrl}/GetGroups`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar grupos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os grupos e retorna apenas os dados mapeados
   */
  getAllGroups(): Observable<Group[]> {
    return this.getGroups(1, 1000).pipe(
      map(response => {
        // Validação para evitar erro de undefined
        if (!response || !response.data) {
          return [];
        }
        return response.data.map(dto => this.mapFromGroupDTO(dto));
      }),
      catchError(err => {
        console.error('❌ [ProfilesService] Erro ao buscar todos os grupos:', err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca permissões por grupo
   */
  getPermissionsByGroup(groupId: number, page: number = 1, pageSize: number = 10): Observable<PermissionApiResponse> {
    const params = new HttpParams()
      .set('groupId', groupId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<PermissionApiResponse>(`${this.baseUrl}/GetPermissions`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar permissões por grupo');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todas as permissões de um grupo e retorna apenas os dados mapeados
   */
  getAllPermissionsByGroup(groupId: number): Observable<Permission[]> {
    return this.getPermissionsByGroup(groupId, 1, 1000).pipe(
      map(response => {
        // Validação para evitar erro de undefined
        if (!response || !response.data) {
          return [];
        }
        return response.data.map(dto => this.mapFromPermissionDTO(dto));
      }),
      catchError(err => {
        console.error(`❌ [ProfilesService] Erro ao buscar permissões do grupo ${groupId}:`, err);
        return throwError(() => err);
      })
    );
  }

  /**
   * Mapeia dados recebidos da API para o modelo Profile
   */
  private mapFromProfileDTO(dto: ProfileDto): Profile {
    return {
      id: dto.Id,
      name: dto.Name,
      description: dto.Description,
      status: dto.Status || 'ativo',
      createdAt: new Date(dto.CreatedAt),
      updatedAt: new Date(dto.UpdatedAt),
      createdBy: dto.CreatedBy,
      updatedBy: dto.UpdatedBy,
      createdByName: dto.CreatedByName,
      updatedByName: dto.UpdatedByName
    };
  }

  /**
   * Mapeia dados recebidos da API para o modelo Group
   */
  private mapFromGroupDTO(dto: GroupDto): Group {
    return {
      id: dto.Id,
      name: dto.Name,
      description: dto.Description,
      profileId: dto.ProfileId,
      createdAt: new Date(dto.CreatedAt),
      updatedAt: new Date(dto.UpdatedAt),
      createdBy: dto.CreatedBy,
      updatedBy: dto.UpdatedBy
    };
  }

  /**
   * Mapeia dados recebidos da API para o modelo Permission
   */
  private mapFromPermissionDTO(dto: PermissionDto): Permission {
    return {
      id: dto.Id,
      name: dto.Name,
      description: dto.Description,
      groupId: dto.GroupId,
      createdAt: new Date(dto.CreatedAt),
      updatedAt: new Date(dto.UpdatedAt),
      createdBy: dto.CreatedBy,
      updatedBy: dto.UpdatedBy
    };
  }

  // ===== Métodos Adicionais da Coleção Postman =====

  /**
   * Cria um novo perfil
   */
  createProfile(profileData: { name: string; description?: string; createdBy: string }): Observable<Profile> {
    return this.http.post<ProfileDto>(`${this.baseUrl}/CreateProfile`, profileData).pipe(
      map(dto => this.mapFromProfileDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualiza um perfil existente
   */
  updateProfile(id: number, profileData: { name?: string; description?: string; updatedBy: string }): Observable<Profile> {
    return this.http.put<ProfileDto>(`${this.baseUrl}/UpdateProfile/${id}`, profileData).pipe(
      map(dto => this.mapFromProfileDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove um perfil (soft delete)
   */
  deleteProfile(id: number, deletedBy: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/DeleteProfile/${id}`, {
      body: { deletedBy }
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao excluir perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca perfil por ID específico
   */
  getProfileByIdSpecific(id: number): Observable<Profile> {
    return this.http.get<ProfileDto>(`${this.baseUrl}/GetProfiles/${id}`).pipe(
      map(dto => this.mapFromProfileDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Valida se o nome do perfil está disponível
   */
  checkProfileNameAvailability(name: string, excludeId?: number): Observable<{ available: boolean }> {
    let params = new HttpParams().set('name', name);
    if (excludeId) {
      params = params.set('excludeId', excludeId.toString());
    }
    
    return this.http.get<{ available: boolean }>(
      `${this.baseUrl}/CheckProfileNameAvailability`, 
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao verificar disponibilidade do nome do perfil');
        return throwError(() => err);
      })
    );
  }

  // ===== Novas Funcionalidades da Coleção Postman =====

  /**
   * Cria um perfil com grupos associados
   */
  createProfileWithGroups(profileData: ProfileWithGroupsCreate): Observable<Profile> {
    return this.http.post<ProfileDto>(`${this.baseUrl}/CreateProfileWithGroups`, profileData).pipe(
      map(dto => this.mapFromProfileDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar perfil com grupos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Cria um perfil com grupos personalizados
   */
  createProfileWithCustomGroups(profileData: ProfileWithCustomGroupsCreate): Observable<Profile> {
    return this.http.post<ProfileDto>(`${this.baseUrl}/CreateProfileWithCustomGroups`, profileData).pipe(
      map(dto => this.mapFromProfileDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar perfil com grupos personalizados');
        return throwError(() => err);
      })
    );
  }

  /**
   * Cria um perfil com permissões específicas
   */
  createProfileWithSpecificPermissions(profileData: ProfileWithSpecificPermissionsCreate): Observable<Profile> {
    return this.http.post<ProfileDto>(`${this.baseUrl}/CreateProfileWithSpecificPermissions`, profileData).pipe(
      map(dto => this.mapFromProfileDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar perfil com permissões específicas');
        return throwError(() => err);
      })
    );
  }

  /**
   * Associa permissões específicas a um perfil existente
   */
  associateSpecificPermissionsToProfile(request: AssociateSpecificPermissionsRequest): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/AssociateSpecificPermissionsToProfile`, request).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao associar permissões específicas ao perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca perfil com grupos e permissões associadas
   */
  getProfileWithGroups(profileId: number): Observable<Profile> {
    return this.http.get<ProfileDto>(`${this.baseUrl}/GetProfileWithGroups/${profileId}`).pipe(
      map(dto => this.mapFromProfileDTO(dto)),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar perfil com grupos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todos os perfis com grupos e permissões associadas
   */
  getProfilesWithGroups(page: number = 1, pageSize: number = 10): Observable<ProfileApiResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfileApiResponse>(`${this.baseUrl}/GetProfilesWithGroups`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar perfis com grupos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Associa grupos a um perfil existente
   */
  associateGroupsToProfile(profileId: number, groupIds: number[], updatedBy: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/AssociateGroupsToProfile`, {
      profileId,
      groupIds,
      updatedBy
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao associar grupos ao perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove grupos de um perfil
   */
  removeGroupsFromProfile(profileId: number, groupIds: number[], updatedBy: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/RemoveGroupsFromProfile`, {
      body: {
        profileId,
        groupIds,
        updatedBy
      }
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover grupos do perfil');
        return throwError(() => err);
      })
    );
  }

  // ===== Debug Functions =====

  /**
   * Busca todas as associações Profile-Group (para debug)
   */
  getProfileGroupAssociations(page: number = 1, pageSize: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<any>(`${this.baseUrl}/GetProfileGroupAssociations`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar associações Profile-Group');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca associações de um perfil específico (para debug)
   */
  getProfileGroupAssociationsByProfile(profileId: number, page: number = 1, pageSize: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('profileId', profileId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<any>(`${this.baseUrl}/GetProfileGroupAssociations`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage(`Erro ao buscar associações do perfil ${profileId}`);
        return throwError(() => err);
      })
    );
  }

  // ===== Batch Operations =====

  /**
   * Cria múltiplos perfis de uma vez
   */
  createMultipleProfiles(profiles: { name: string; description?: string; createdBy: string }[]): Observable<Profile[]> {
    return this.http.post<ProfileDto[]>(`${this.baseUrl}/CreateMultipleProfiles`, profiles).pipe(
      map(dtos => dtos.map(dto => this.mapFromProfileDTO(dto))),
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar múltiplos perfis');
        return throwError(() => err);
      })
    );
  }

  /**
   * Deleta múltiplos perfis de uma vez
   */
  deleteMultipleProfiles(profileIds: number[], deletedBy: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/DeleteMultipleProfiles`, {
      body: { profileIds, deletedBy }
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao excluir múltiplos perfis');
        return throwError(() => err);
      })
    );
  }

  // ===== Advanced Search =====

  /**
   * Busca perfis por nome ou descrição
   */
  searchProfiles(searchTerm: string, page: number = 1, pageSize: number = 10): Observable<ProfileApiResponse> {
    const params = new HttpParams()
      .set('search', searchTerm)
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfileApiResponse>(`${this.baseUrl}/SearchProfiles`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar perfis');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca perfis ativos
   */
  getActiveProfiles(page: number = 1, pageSize: number = 10): Observable<ProfileApiResponse> {
    const params = new HttpParams()
      .set('status', 'ativo')
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfileApiResponse>(`${this.baseUrl}/GetProfiles`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar perfis ativos');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca perfis inativos
   */
  getInactiveProfiles(page: number = 1, pageSize: number = 10): Observable<ProfileApiResponse> {
    const params = new HttpParams()
      .set('status', 'inativo')
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfileApiResponse>(`${this.baseUrl}/GetProfiles`, { params }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar perfis inativos');
        return throwError(() => err);
      })
    );
  }

  // ===== Export/Import =====

  /**
   * Exporta perfis para backup
   */
  exportProfiles(format: 'json' | 'csv' = 'json'): Observable<Blob> {
    const params = new HttpParams().set('format', format);
    
    return this.http.get(`${this.baseUrl}/ExportProfiles`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao exportar perfis');
        return throwError(() => err);
      })
    );
  }
}
